// Sprint 0082 → HR-004 follow-up — Manpower Norms Workbench.
//
// What changed (vs the original flat table):
//   * Grouped by Department → Machine. Each machine card shows the norm
//     rows for that machine (per-machine wins over type-level by precedence).
//   * Inline edit on count / skill / critical without opening a modal.
//   * Explode action: type-level norm → per-machine rows for every machine
//     of that type. Lets a mill migrate from "1 default" to "24 customised".
//   * Soft-delete + Hard-delete (the latter only when no allocation refs).
//   * Filter chip "Needs review" — surfaces auto-spawned rows from
//     machine creation that haven't been confirmed by a human yet.
//   * New Norm modal supports BOTH per-machine and per-type creation.

import { useMemo, useState } from 'react'
import {
  App, Alert, Button, Card, DatePicker, Empty, Form, Input, InputNumber,
  Modal, Popconfirm, Select, Space, Switch, Tag, Tooltip, Typography,
} from 'antd'
import {
  DeleteOutlined, EditOutlined, ExpandAltOutlined, PlusOutlined,
  ReloadOutlined, ToolOutlined, WarningOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  normsApi, WORKER_ROLES, MACHINE_TYPES,
  type ManpowerNorm, type NormPatchBody,
} from '@/features/hr/manpowerApi'
import { departmentsApi } from '@/features/masters/departments/api'
import { machinesApi } from '@/features/masters/machines/api'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import type { Machine } from '@/shared/types/master'

const { Text } = Typography

interface Department {
  id: string
  name: string
}

// A "norm bucket" — one machine's set of norm rows. NULL machine_id = the
// type-level template; everything else = a real machine.
interface NormBucket {
  key: string
  department_id: string
  department_name: string
  machine: Machine | null
  machine_label: string
  machine_type: string
  norms: ManpowerNorm[]
}

const ROLE_COLOR: Record<string, string> = {
  OPERATOR: 'blue', HELPER: 'cyan', JOBBER: 'gold',
  GAITER: 'purple', SWEEPER: 'lime', RELIEVER: 'magenta',
  FITTER: 'orange', ELECTRICIAN: 'volcano',
  ELECTRONIC_TECHNICIAN: 'pink', UTILITY_OPERATOR: 'geekblue',
  SUPERVISOR: 'red', SHIFT_INCHARGE: 'red',
}

function isNeedsReview(n: ManpowerNorm): boolean {
  return !!n.remarks && n.remarks.includes('auto-from-template')
}

export default function ManpowerNormsPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const { plantId } = usePlantId()

  const [deptFilter, setDeptFilter] = useState<string | undefined>()
  const [showOnlyReview, setShowOnlyReview] = useState(false)
  const [showInactive, setShowInactive] = useState(false)

  const [createOpen, setCreateOpen] = useState(false)
  const [editingNorm, setEditingNorm] = useState<ManpowerNorm | null>(null)

  // ─── Data ─────────────────────────────────────────────────────────────
  const { data: depts } = useQuery({
    queryKey: ['mn-depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 500, plant_id: plantId }),
    enabled: !!plantId,
  })
  const { data: machines } = useQuery({
    queryKey: ['mn-machines', plantId, deptFilter],
    queryFn: () => machinesApi.list({
      plant_id: plantId, department_id: deptFilter,
      is_active: true, page: 1, page_size: 500,
    }),
    enabled: !!plantId,
  })
  const { data: norms, isLoading, refetch } = useQuery({
    queryKey: ['mn-norms', plantId, deptFilter, showInactive],
    queryFn: () => normsApi.list({
      plant_id: plantId,
      department_id: deptFilter,
      is_active: showInactive ? undefined : true,
    }),
    enabled: !!plantId,
  })

  // ─── Buckets — group norms by machine for display ───────────────────
  const buckets: NormBucket[] = useMemo(() => {
    const machineById = new Map<string, Machine>()
    for (const m of (machines?.items ?? [])) machineById.set(m.id, m)
    const deptById = new Map<string, Department>()
    for (const d of (depts?.items ?? [])) deptById.set(d.id, d)

    // group by (department_id, machine_id || `type:${machine_type}`)
    const groups = new Map<string, NormBucket>()
    for (const n of (norms ?? [])) {
      if (showOnlyReview && !isNeedsReview(n)) continue
      const machine = n.machine_id ? machineById.get(n.machine_id) ?? null : null
      const dept = deptById.get(n.department_id)
      const key = `${n.department_id}::${n.machine_id ?? `type:${n.machine_type}`}`
      let bucket = groups.get(key)
      if (!bucket) {
        bucket = {
          key,
          department_id: n.department_id,
          department_name: dept?.name ?? '—',
          machine,
          machine_label: machine
            ? `${machine.machine_id_str} · ${machine.name}`
            : `Type default — ${n.machine_type}`,
          machine_type: n.machine_type,
          norms: [],
        }
        groups.set(key, bucket)
      }
      bucket.norms.push(n)
    }
    return Array.from(groups.values()).sort((a, b) => {
      // Sort: department asc → type-defaults first (machine is null) → machine_code asc
      if (a.department_name !== b.department_name) {
        return a.department_name.localeCompare(b.department_name)
      }
      const aIsType = !a.machine
      const bIsType = !b.machine
      if (aIsType !== bIsType) return aIsType ? -1 : 1
      return (a.machine?.machine_id_str ?? '').localeCompare(
        b.machine?.machine_id_str ?? '')
    })
  }, [norms, machines, depts, showOnlyReview])

  // ─── KPIs ────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total = (norms ?? []).length
    const typeLevel = (norms ?? []).filter((n) => !n.machine_id).length
    const perMachine = total - typeLevel
    const needsReview = (norms ?? []).filter(isNeedsReview).length
    const machinesCovered = new Set(
      (norms ?? []).filter((n) => n.machine_id).map((n) => n.machine_id))
    const totalMachines = machines?.items?.length ?? 0
    return { total, typeLevel, perMachine, needsReview,
             machinesCovered: machinesCovered.size, totalMachines }
  }, [norms, machines])

  // ─── Mutations ───────────────────────────────────────────────────────
  const invalidate = () => qc.invalidateQueries({ queryKey: ['mn-norms'] })

  const patchMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: NormPatchBody }) =>
      normsApi.patch(id, body),
    onSuccess: () => { message.success('Norm updated'); invalidate() },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Update failed')
    },
  })

  const removeMutation = useMutation({
    mutationFn: ({ id, hard }: { id: string; hard: boolean }) =>
      normsApi.remove(id, hard),
    onSuccess: (_, vars) => {
      message.success(vars.hard ? 'Norm deleted' : 'Norm deactivated')
      invalidate()
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Delete failed')
    },
  })

  const explodeMutation = useMutation({
    mutationFn: (id: string) => normsApi.explode(id, true),
    onSuccess: (rows) => {
      message.success(`Created ${rows.length} per-machine norm row${rows.length === 1 ? '' : 's'}`)
      invalidate()
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Explode failed')
    },
  })

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <div className="hr-mobile-root">
      <PageHeader
        title="Manpower Norms — Workbench"
        subtitle="Per-machine plan: how many workers each machine needs by role. Drives the allocation board."
        extra={canEdit && (
          <Space>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={() => refetch()} />
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}>
              New Norm
            </Button>
          </Space>
        )}
      />

      {/* KPI strip */}
      <div style={{
        display: 'grid', gap: 8, marginBottom: 12,
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      }}>
        <KPI value={kpis.total} label="Active norms" />
        <KPI value={kpis.typeLevel} label="Type defaults" />
        <KPI value={kpis.perMachine} label="Per-machine" />
        <KPI value={`${kpis.machinesCovered}/${kpis.totalMachines}`} label="Machines covered" />
        <KPI value={kpis.needsReview} label="Need review"
             variant={kpis.needsReview > 0 ? 'amber' : 'neutral'} />
      </div>

      {/* Filters */}
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          allowClear placeholder="Department" style={{ minWidth: 220 }}
          value={deptFilter} onChange={setDeptFilter}
          options={(depts?.items ?? []).map((d) => ({ label: d.name, value: d.id }))}
        />
        <Tag.CheckableTag checked={showOnlyReview}
          onChange={setShowOnlyReview}
          style={{ padding: '4px 12px', height: 32, lineHeight: '24px' }}>
          {showOnlyReview ? '✓ ' : ''}Show only “Needs review”
        </Tag.CheckableTag>
        <Tag.CheckableTag checked={showInactive}
          onChange={setShowInactive}
          style={{ padding: '4px 12px', height: 32, lineHeight: '24px' }}>
          {showInactive ? '✓ ' : ''}Include deactivated
        </Tag.CheckableTag>
      </Space>

      {/* Empty state */}
      {isLoading && <Empty description="Loading…" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      {!isLoading && buckets.length === 0 && (
        <Empty
          description={showOnlyReview
            ? 'No norms flagged for review.'
            : 'No norms in scope. Create one to start.'}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {/* Buckets — one card per machine */}
      <div style={{
        display: 'grid', gap: 12,
        gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
      }}>
        {buckets.map((b) => (
          <NormBucketCard
            key={b.key}
            bucket={b}
            canEdit={canEdit}
            onEdit={(n) => setEditingNorm(n)}
            onDelete={(n, hard) => removeMutation.mutate({ id: n.id, hard })}
            onExplode={(n) => explodeMutation.mutate(n.id)}
            onPatch={(n, body) => patchMutation.mutate({ id: n.id, body })}
          />
        ))}
      </div>

      {/* Create modal */}
      <CreateNormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        depts={(depts?.items ?? []) as Department[]}
        machines={machines?.items ?? []}
        onSaved={() => { setCreateOpen(false); invalidate() }}
      />

      {/* Edit modal — fall-back for fields not editable inline */}
      <EditNormModal
        norm={editingNorm}
        onClose={() => setEditingNorm(null)}
        onSaved={() => { setEditingNorm(null); invalidate() }}
      />
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────────

function KPI({ value, label, variant = 'neutral' }: {
  value: number | string; label: string
  variant?: 'neutral' | 'green' | 'amber' | 'red'
}) {
  return (
    <div className={`threshold-tile threshold-tile--${variant}`}>
      <div className="threshold-tile__value">{value}</div>
      <div className="threshold-tile__label">{label}</div>
    </div>
  )
}

function NormBucketCard({
  bucket, canEdit, onEdit, onDelete, onExplode, onPatch,
}: {
  bucket: NormBucket
  canEdit: boolean
  onEdit: (n: ManpowerNorm) => void
  onDelete: (n: ManpowerNorm, hard: boolean) => void
  onExplode: (n: ManpowerNorm) => void
  onPatch: (n: ManpowerNorm, body: NormPatchBody) => void
}) {
  const isTypeBucket = !bucket.machine
  const totalRequired = bucket.norms.reduce(
    (sum, n) => sum + Number(n.required_count || 0), 0)
  const hasNeedsReview = bucket.norms.some(isNeedsReview)

  return (
    <Card
      size="small"
      style={{
        border: hasNeedsReview ? '1.5px solid #faad14' : undefined,
      }}
      title={
        <Space size={6} style={{ alignItems: 'center' }}>
          <ToolOutlined style={{ color: isTypeBucket ? '#fa8c16' : '#1677ff' }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              {bucket.machine_label}
            </div>
            <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 400 }}>
              {bucket.department_name} · {bucket.machine_type}
            </div>
          </div>
        </Space>
      }
      extra={
        <Space size={4}>
          {isTypeBucket && (
            <Tag color="orange" style={{ margin: 0 }}>TEMPLATE</Tag>
          )}
          <Tag>{Number.isInteger(totalRequired) ? totalRequired : totalRequired.toFixed(2)} reqd</Tag>
        </Space>
      }
    >
      {isTypeBucket && (
        <Alert
          type="warning" showIcon icon={<ExpandAltOutlined />}
          style={{ marginBottom: 10, fontSize: 12 }}
          message={<><b>This is a type default.</b> It applies to every machine
            of type <code>{bucket.machine_type}</code> in this dept unless that
            machine has its own per-machine norm. Use “Explode” to convert it
            into one row per machine that you can edit individually.</>}
        />
      )}

      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        {bucket.norms.map((n) => (
          <NormRow
            key={n.id} norm={n}
            isTemplate={isTypeBucket}
            canEdit={canEdit}
            onEdit={() => onEdit(n)}
            onDelete={(hard) => onDelete(n, hard)}
            onExplode={() => onExplode(n)}
            onPatch={(body) => onPatch(n, body)}
          />
        ))}
      </Space>
    </Card>
  )
}

function NormRow({
  norm, isTemplate, canEdit, onEdit, onDelete, onExplode, onPatch,
}: {
  norm: ManpowerNorm
  isTemplate: boolean
  canEdit: boolean
  onEdit: () => void
  onDelete: (hard: boolean) => void
  onExplode: () => void
  onPatch: (body: NormPatchBody) => void
}) {
  const needsReview = isNeedsReview(norm)
  const inactive = !norm.is_active

  // Inline-edit state for required_count & skill (the most-edited fields)
  const [editingCount, setEditingCount] = useState(false)
  const [countDraft, setCountDraft] = useState<number>(Number(norm.required_count))

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 8px', borderRadius: 8,
      background: needsReview ? '#fffbe6' : inactive ? '#fafafa' : '#fff',
      border: '1px solid #f0f0f0',
      opacity: inactive ? 0.55 : 1,
    }}>
      {/* Role + criticality */}
      <Tag color={ROLE_COLOR[norm.role_required] ?? 'default'} style={{ margin: 0 }}>
        {norm.role_required}
      </Tag>
      {norm.critical_post_flag && <Tag color="red" style={{ margin: 0 }}>CRIT</Tag>}
      {norm.certification_required && <Tag color="purple" style={{ margin: 0 }}>CERT</Tag>}

      {/* Required count — inline editable */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
        {editingCount ? (
          <Space size={4}>
            <InputNumber
              size="small" min={0} step={0.25} style={{ width: 80 }}
              value={countDraft}
              onChange={(v) => setCountDraft(Number(v ?? 0))}
              autoFocus
            />
            <Button size="small" type="primary"
              onClick={() => {
                onPatch({ required_count: countDraft })
                setEditingCount(false)
              }}>OK</Button>
            <Button size="small"
              onClick={() => {
                setCountDraft(Number(norm.required_count))
                setEditingCount(false)
              }}>×</Button>
          </Space>
        ) : (
          <Space size={4}>
            <Text strong style={{ fontSize: 13 }}>
              {Number(norm.required_count).toFixed(2).replace(/\.00$/, '')}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              × Skill ≥ {norm.required_skill_level}
            </Text>
            {canEdit && (
              <Tooltip title="Click count to edit">
                <EditOutlined
                  style={{ fontSize: 10, color: '#bfbfbf', cursor: 'pointer' }}
                  onClick={() => setEditingCount(true)}
                />
              </Tooltip>
            )}
          </Space>
        )}
      </div>

      {needsReview && (
        <Tooltip title="Auto-spawned from template — please confirm the count is right for this machine">
          <Tag color="gold" icon={<WarningOutlined />} style={{ margin: 0 }}>REVIEW</Tag>
        </Tooltip>
      )}

      {/* Actions */}
      {canEdit && (
        <Space size={2}>
          <Tooltip title="Edit all fields">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={onEdit} />
          </Tooltip>
          {isTemplate && (
            <Tooltip title="Convert this default into one editable row per machine">
              <Popconfirm
                title="Explode template into per-machine rows?"
                description="Creates one norm row per active machine of this type+dept, and deactivates this template so it doesn't double-count."
                onConfirm={onExplode}
                okText="Explode" cancelText="Cancel"
              >
                <Button size="small" type="text" icon={<ExpandAltOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
          <Popconfirm
            title={`Deactivate this norm?`}
            description="Allocations referencing it stay intact. You can re-activate later."
            onConfirm={() => onDelete(false)}
            okText="Deactivate" okButtonProps={{ danger: true }}
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )}
    </div>
  )
}

// ─── Modals ──────────────────────────────────────────────────────────────

function CreateNormModal({
  open, onClose, depts, machines, onSaved,
}: {
  open: boolean
  onClose: () => void
  depts: Department[]
  machines: Machine[]
  onSaved: () => void
}) {
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const [deptId, setDeptId] = useState<string | undefined>()
  const machineOptions = useMemo(
    () => machines.filter((m) => !deptId || m.department_id === deptId),
    [machines, deptId])

  const submit = async () => {
    try {
      const v = await form.validateFields()
      await normsApi.create({
        plant_id: '',  // backend derives from dept ; we'll pass via department
        department_id: v.department_id,
        section_code: v.section_code || null,
        machine_id: v.machine_id || null,
        machine_type: v.machine_type,
        shift_id: null,
        role_required: v.role_required,
        required_skill_level: v.required_skill_level ?? 0,
        required_count: String(v.required_count ?? 1),
        critical_post_flag: !!v.critical_post_flag,
        certification_required: !!v.certification_required,
        effective_from: v.effective_from?.format('YYYY-MM-DD'),
        effective_to: v.effective_to?.format('YYYY-MM-DD') ?? null,
      } as never)
      message.success('Norm created')
      form.resetFields()
      onSaved()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      if (err?.response?.data?.detail) message.error(err.response.data.detail)
    }
  }

  return (
    <Modal
      open={open} onCancel={onClose} onOk={submit}
      title="New Manpower Norm" width={560} okText="Create"
    >
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Per-machine vs Type default"
        description="Pick a specific Machine to set a norm just for that machine. Leave Machine empty to set a type default (applies to all machines of that type in the dept that don't have their own row)."
      />
      <Form form={form} layout="vertical" initialValues={{
        required_skill_level: 0, required_count: 1,
        effective_from: dayjs(),
      }}>
        <Form.Item name="department_id" label="Department" rules={[{ required: true }]}>
          <Select
            options={depts.map((d) => ({ label: d.name, value: d.id }))}
            onChange={setDeptId}
          />
        </Form.Item>
        <Form.Item name="machine_type" label="Machine Type" rules={[{ required: true }]}>
          <Select options={MACHINE_TYPES.map((m) => ({ label: m, value: m }))} />
        </Form.Item>
        <Form.Item name="machine_id" label="Machine (leave empty for type default)">
          <Select allowClear showSearch placeholder="Pick a machine — or leave empty"
            options={machineOptions.map((m) => ({
              label: `${m.machine_id_str} · ${m.name}`, value: m.id,
            }))}
            filterOption={(input, opt) =>
              ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <Form.Item name="section_code" label="Section (optional)">
          <Input placeholder="e.g. Cards A-line" />
        </Form.Item>
        <Form.Item name="role_required" label="Role" rules={[{ required: true }]}>
          <Select options={WORKER_ROLES.map((r) => ({ label: r, value: r }))} />
        </Form.Item>
        <Form.Item name="required_count" label="Required Count"
          tooltip="1 = one worker on this machine. 0.5 = shared across 2 machines.">
          <InputNumber min={0} step={0.25} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="required_skill_level" label="Minimum Skill Level (0-5)">
          <InputNumber min={0} max={5} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="critical_post_flag" label="Critical Post" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="certification_required" label="Certification Required" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="effective_from" label="Effective From" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="effective_to" label="Effective Till (optional)">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

function EditNormModal({
  norm, onClose, onSaved,
}: {
  norm: ManpowerNorm | null
  onClose: () => void
  onSaved: () => void
}) {
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const open = !!norm

  useMemo(() => {
    if (norm) {
      form.setFieldsValue({
        required_count: Number(norm.required_count),
        required_skill_level: norm.required_skill_level,
        critical_post_flag: norm.critical_post_flag,
        certification_required: norm.certification_required,
        role_required: norm.role_required,
        section_code: norm.section_code,
        remarks: norm.remarks,
        effective_from: dayjs(norm.effective_from),
        effective_to: norm.effective_to ? dayjs(norm.effective_to) : null,
      })
    }
  }, [norm, form])

  const submit = async () => {
    if (!norm) return
    try {
      const v = await form.validateFields()
      const body: NormPatchBody = {
        required_count: Number(v.required_count),
        required_skill_level: v.required_skill_level,
        critical_post_flag: v.critical_post_flag,
        certification_required: v.certification_required,
        role_required: v.role_required,
        section_code: v.section_code || null,
        remarks: v.remarks || null,
        effective_from: v.effective_from?.format('YYYY-MM-DD'),
        effective_to: v.effective_to?.format('YYYY-MM-DD') ?? null,
      }
      await normsApi.patch(norm.id, body)
      message.success('Norm updated')
      onSaved()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      if (err?.response?.data?.detail) message.error(err.response.data.detail)
    }
  }

  return (
    <Modal
      open={open} onCancel={onClose} onOk={submit}
      title="Edit Norm" width={520} okText="Save"
    >
      {norm && (
        <Form form={form} layout="vertical">
          <Form.Item name="role_required" label="Role">
            <Select options={WORKER_ROLES.map((r) => ({ label: r, value: r }))} />
          </Form.Item>
          <Form.Item name="required_count" label="Required Count">
            <InputNumber min={0} step={0.25} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="required_skill_level" label="Minimum Skill Level">
            <InputNumber min={0} max={5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="section_code" label="Section (optional)">
            <Input placeholder="e.g. Cards A-line" />
          </Form.Item>
          <Form.Item name="critical_post_flag" label="Critical Post" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="certification_required" label="Certification Required" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="effective_from" label="Effective From">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="effective_to" label="Effective Till">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks (e.g. clear the auto-spawn flag)">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
